import { Text } from "@solomon-labs/design/components/ui";
import type { FormEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { Link } from "react-router-dom";

import { useOrgAuthStore } from "../../stores/useOrgAuthStore";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Spinner } from "../ui/spinner";

import {
    ORG_AUTH_CLAIM_NETWORK_ERROR,
    ORG_AUTH_CLAIM_PASSWORD_LENGTH_ERROR,
    type OrgAuthClaimErrorBody,
    parseOrgAuthClaimToken,
    resolveOrgAuthClaimError
} from "./orgAuthClaimErrors";
import {
    AUTH_INPUT_CLASS,
    AUTH_READONLY_INPUT_CLASS,
    OrgAuthField,
    OrgAuthShell
} from "./OrgAuthShell";

interface OrgAuthClaimPageProps {
    apiBase: string;
    appLabel: string;
}

interface ClaimPreview {
    orgName: string;
    email: string;
    purpose: "InitialClaim" | "PasswordReset";
}

type ClaimState =
    | { phase: "loading" }
    | { phase: "form"; preview: ClaimPreview; formError?: string }
    | { phase: "submitting"; preview: ClaimPreview }
    | { phase: "success" }
    | { phase: "error"; message: string; retryable?: boolean };

const FORM_ERROR_ID = "org-auth-claim-form-error";
const PASSWORD_ERROR_ID = "org-auth-claim-password-error";

function ClaimSignInLink(): React.ReactElement {
    return (
        <Text
            variant="small"
            className="text-foreground-base underline underline-offset-2 hover:text-foreground-muted"
            render={<Link to="/login" />}
        >
            Go to sign in
        </Text>
    );
}

export function OrgAuthClaimPage({
    apiBase,
    appLabel: _appLabel
}: OrgAuthClaimPageProps): React.ReactElement {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [validationError, setValidationError] = useState<string | null>(null);
    const [state, setState] = useState<ClaimState>({ phase: "loading" });
    const successLinkRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        if (state.phase === "success") {
            successLinkRef.current?.focus();
        }
    }, [state.phase]);

    const token = useMemo(
        () => parseOrgAuthClaimToken(window.location.hash),
        []
    );

    useEffect(() => {
        if (token && window.location.hash) {
            window.history.replaceState(null, "", window.location.pathname);
        }
    }, [token]);

    const loadPreview = useCallback(
        async (isCancelled: () => boolean = () => false): Promise<void> => {
            if (!token) return;

            setState({ phase: "loading" });

            try {
                const response = await fetch(
                    `${apiBase}/api/org/claim/preview`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            tokenUuid: token.tokenUuid,
                            secret: token.secret
                        })
                    }
                );

                if (isCancelled()) return;

                if (response.ok) {
                    const preview = (await response.json()) as ClaimPreview;
                    setState({ phase: "form", preview });
                    return;
                }

                const body = (await response
                    .json()
                    .catch(() => ({}))) as OrgAuthClaimErrorBody;
                const resolved = resolveOrgAuthClaimError(
                    response.status,
                    body,
                    "preview"
                );

                setState({
                    phase: "error",
                    message: resolved.message,
                    retryable: resolved.retryable
                });
            } catch {
                if (isCancelled()) return;
                setState({
                    phase: "error",
                    message: ORG_AUTH_CLAIM_NETWORK_ERROR,
                    retryable: true
                });
            }
        },
        [apiBase, token]
    );

    useEffect(() => {
        if (!token) return;

        let cancelled = false;
        void loadPreview(() => cancelled);

        return (): void => {
            cancelled = true;
        };
    }, [token, loadPreview]);

    if (!token) {
        return (
            <OrgAuthShell
                title="Invalid link"
                intro="This link is missing or malformed. Make sure you copied the full URL."
            >
                <div className="text-center">
                    <ClaimSignInLink />
                </div>
            </OrgAuthShell>
        );
    }

    if (state.phase === "loading") {
        return (
            <OrgAuthShell intro="Verifying your invitation…">
                <div className="flex items-center justify-center py-4">
                    <Spinner />
                </div>
            </OrgAuthShell>
        );
    }

    if (state.phase === "success") {
        return (
            <OrgAuthShell
                title="You're all set"
                intro="Your password has been set. You can now sign in."
            >
                <Button
                    variant="raised"
                    interaction="lifted"
                    size="md"
                    fullWidth
                    render={<Link ref={successLinkRef} to="/login" />}
                >
                    Sign in
                </Button>
            </OrgAuthShell>
        );
    }

    if (state.phase === "error") {
        return (
            <OrgAuthShell title="Unable to continue" intro={state.message}>
                <div className="flex flex-col items-center gap-3 text-center">
                    {state.retryable && (
                        <Button
                            type="button"
                            variant="raised"
                            interaction="lifted"
                            size="md"
                            onClick={() => void loadPreview()}
                        >
                            Try again
                        </Button>
                    )}
                    <ClaimSignInLink />
                </div>
            </OrgAuthShell>
        );
    }

    const { preview } = state;
    const isSubmitting = state.phase === "submitting";
    const isReset = preview.purpose === "PasswordReset";
    const formError = state.phase === "form" ? state.formError : undefined;

    const clearRecoverableErrors = (): void => {
        setValidationError(null);
        setState((current) =>
            current.phase === "form" && current.formError
                ? { phase: "form", preview: current.preview }
                : current
        );
    };

    const handleSubmit = async (e: FormEvent): Promise<void> => {
        e.preventDefault();
        setValidationError(null);

        if (password.length < 8 || password.length > 72) {
            setValidationError(ORG_AUTH_CLAIM_PASSWORD_LENGTH_ERROR);
            return;
        }
        if (password !== confirmPassword) {
            setValidationError("Passwords do not match.");
            return;
        }

        setState({ phase: "submitting", preview });

        try {
            const response = await fetch(`${apiBase}/api/org/claim`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    tokenUuid: token.tokenUuid,
                    secret: token.secret,
                    password
                })
            });

            if (response.ok) {
                // Claiming an account is an identity switch: any org session
                // already in this tab belongs to someone else (or, for a
                // password reset, to tokens the backend just invalidated by
                // rotating the oauthSubject). Clear it so the Sign in link
                // lands on the login form instead of silently redirecting
                // into the previous user's console.
                useOrgAuthStore.getState().clearAuth();
                setState({ phase: "success" });
                return;
            }

            const body = (await response
                .json()
                .catch(() => ({}))) as OrgAuthClaimErrorBody;
            const resolved = resolveOrgAuthClaimError(
                response.status,
                body,
                "submit"
            );

            if (resolved.scope === "field") {
                setValidationError(resolved.message);
                setState({ phase: "form", preview });
                return;
            }

            if (resolved.scope === "form") {
                setState({
                    phase: "form",
                    preview,
                    formError: resolved.message
                });
                return;
            }

            setState({
                phase: "error",
                message: resolved.message,
                retryable: resolved.retryable
            });
        } catch {
            setState({
                phase: "form",
                preview,
                formError: ORG_AUTH_CLAIM_NETWORK_ERROR
            });
        }
    };

    const passwordDescribedBy = validationError ? PASSWORD_ERROR_ID : undefined;

    return (
        <OrgAuthShell
            title={isReset ? "Reset your password" : "Set your password"}
            intro={
                isReset
                    ? "Choose a new password for your account."
                    : "Choose a password to activate your account."
            }
            footerNote="Access is restricted to authorised team members only."
        >
            <form
                onSubmit={(e) => void handleSubmit(e)}
                className="flex flex-col gap-6"
            >
                <div className="flex flex-col gap-4">
                    <OrgAuthField id="org" label="Organization">
                        <Input
                            id="org"
                            type="text"
                            readOnly
                            tabIndex={-1}
                            value={preview.orgName}
                            surface="filled"
                            size="md"
                            className={AUTH_READONLY_INPUT_CLASS}
                            aria-readonly="true"
                        />
                    </OrgAuthField>

                    <OrgAuthField id="email" label="Email">
                        <Input
                            id="email"
                            type="email"
                            readOnly
                            tabIndex={-1}
                            value={preview.email}
                            surface="filled"
                            size="md"
                            className={AUTH_READONLY_INPUT_CLASS}
                            aria-readonly="true"
                        />
                    </OrgAuthField>

                    <OrgAuthField
                        id="password"
                        label={isReset ? "New password" : "Password"}
                    >
                        <Input
                            id="password"
                            type="password"
                            autoComplete="new-password"
                            required
                            minLength={8}
                            maxLength={72}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                clearRecoverableErrors();
                            }}
                            surface="filled"
                            size="md"
                            state={validationError ? "error" : undefined}
                            aria-describedby={passwordDescribedBy}
                            placeholder="Min. 8 characters"
                            disabled={isSubmitting}
                            className={AUTH_INPUT_CLASS}
                        />
                    </OrgAuthField>

                    <OrgAuthField id="confirmPassword" label="Confirm password">
                        <Input
                            id="confirmPassword"
                            type="password"
                            autoComplete="new-password"
                            required
                            minLength={8}
                            maxLength={72}
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                clearRecoverableErrors();
                            }}
                            surface="filled"
                            size="md"
                            state={validationError ? "error" : undefined}
                            aria-describedby={passwordDescribedBy}
                            placeholder="Re-enter password"
                            disabled={isSubmitting}
                            className={AUTH_INPUT_CLASS}
                        />
                    </OrgAuthField>

                    {validationError && (
                        <Text
                            id={PASSWORD_ERROR_ID}
                            variant="small"
                            role="alert"
                            className="text-error-base text-pretty"
                        >
                            {validationError}
                        </Text>
                    )}

                    {formError && (
                        <Text
                            id={FORM_ERROR_ID}
                            variant="small"
                            role="alert"
                            aria-live="polite"
                            className="text-error-base text-pretty"
                        >
                            {formError}
                        </Text>
                    )}
                </div>

                <Button
                    type="submit"
                    variant="raised"
                    interaction="lifted"
                    size="md"
                    fullWidth
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                            <Spinner surface="dark" className="size-3.5" />
                            Setting password
                        </span>
                    ) : isReset ? (
                        "Reset password"
                    ) : (
                        "Set password"
                    )}
                </Button>
            </form>
        </OrgAuthShell>
    );
}
